import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewChild,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FolderDto, GalleryDto } from '@nusign/picflow-backend-types';
import { map } from 'rxjs';
import { FolderService } from '../../../services/folder.service';
import { GalleryService } from '../../../services/gallery.service';
import { FolderBreadcrumbComponent } from '../../../shared/components/folder-breadcrumb/folder-breadcrumb.component';
import { FinderContentComponent } from '../finder-content/finder-content.component';

@Component({
  selector: 'app-finder',
  templateUrl: './finder.component.html',
  styleUrls: ['./finder.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, ReactiveFormsModule, FinderContentComponent, FolderBreadcrumbComponent],
})
export class FinderComponent {
  private folderService = inject(FolderService);
  private galleryService = inject(GalleryService);
  onSelectFolder = output<FolderDto>();
  onSelectGallery = output<GalleryDto>();
  readonly blockGallerySelection = input(false);
  readonly allowFolderAndGalleryAsTarget = input(false);
  readonly folderMode = input(false);
  readonly hideSubGalleries = input(false);
  readonly currentFolder = input<FolderDto>(undefined);
  readonly currentGallery = input<GalleryDto>(undefined);
  readonly disableList = input<(FolderDto | GalleryDto)[]>([]);
  readonly filterOut = input<(FolderDto | GalleryDto)[]>([]);
  readonly reOpenFn = input<(...args) => void>(undefined);
  @ViewChild('searchField', { static: false, read: ElementRef }) searchField: ElementRef<HTMLInputElement>;

  allFolders = toSignal(this.folderService.getFolders());
  allGalleries = toSignal(
    this.galleryService.getAllGalleriesIncludingSubgalleries({ ignorePublicAccessLevel: true }).pipe(
      map(galleries => {
        return galleries.filter(g => !g.access_level || g.access_level === 'edit');
      })
    )
  );
  noResults = signal(false);

  upFolder() {
    if (!this.allFolders()) return;
    const currentFolder = this.currentFolder();
    if (!currentFolder) return;
    if (!currentFolder.parent_folder) this.selectFolder(undefined);
    else this.selectFolder(this.allFolders().find(f => f.id === this.currentFolder().parent_folder));
  }

  selectFolder(folder: FolderDto) {
    this.onSelectFolder.emit(folder);
    if (this.allowFolderAndGalleryAsTarget()) {
      this.selectGallery(undefined);
    } else {
      this.onSelectGallery.emit(undefined);
    }
    return false;
  }

  selectGallery(gallery: GalleryDto) {
    if (gallery?.id === this.currentGallery()?.id) gallery = undefined;
    this.onSelectGallery.emit(gallery);
    return false;
  }
}
